/* eslint-disable react/no-unused-state */
import React, { useState, useCallback, Suspense, useTransition } from 'react'
import LogRocket from 'logrocket'
import environment from '_environments'
import ErrorFallback from './ErrorFallback.jsx'
import Loading from './Loading'

const ErrorBoundary = ({
  refreshPage = true,
  fallback,
  fallbackRender,
  children,
}) => {
  const [hasError, setHasError] = useState(false)
  const [error, setError] = useState(null)
  const [rawError, setRawError] = useState(null)
  const [isPending, startTransition] = useTransition()
  const [errorLogged, setErrorLogged] = useState(false)

  // Use startTransition for non-urgent UI updates
  const handleError = useCallback(
    (err, info) => {
      try {
        const details = info.componentStack.trim().split('\n')
        const errorDetails = {
          code: err.number,
          file: err.fileName,
          line: err.lineNumber,
          message: err.message.toString() + ' ' + details.shift(),
          details,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        }

        // Wrap non-urgent state updates in startTransition
        startTransition(() => {
          setRawError(err)
          setError(errorDetails)
          setHasError(true)
        })

        // Only log to LogRocket if this error hasn't been logged yet
        if (!errorLogged && environment.keys.logrocket) {
          LogRocket.captureException(err, {
            extra: errorDetails,
          })
          setErrorLogged(true)
        }

        // Immediate logging operations (don't need startTransition)
        console.group('Fatal Error Details')
        console.error('Error:', err)
        console.error('Component Stack:', info.componentStack)
        console.error('Additional Details:', errorDetails)
        console.groupEnd()
      } catch (loggingError) {
        console.error('Error while handling error:', loggingError)
      }
    },
    [errorLogged]
  )

  // Show loading state during transition
  if (isPending) {
    return <Loading />
  }

  if (hasError) {
    if (fallbackRender) {
      return fallbackRender({ error, rawError, refreshPage })
    }
    return (
      <Suspense fallback={<Loading />}>
        {fallback || <ErrorFallback refreshPage={refreshPage} error={error} />}
      </Suspense>
    )
  }

  return (
    <ErrorBoundaryWrapper onError={handleError}>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </ErrorBoundaryWrapper>
  )
}

class ErrorBoundaryWrapper extends React.Component {
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    }
  }

  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  componentDidCatch(error, errorInfo) {
    const { onError } = this.props
    this.setState({
      errorInfo,
    })
    onError(error, errorInfo)
  }

  componentWillUnmount() {
    const { hasError } = this.state
    if (hasError) {
      console.log('ErrorBoundary unmounting, cleaning up...')
    }
  }

  render() {
    const { hasError } = this.state
    const { children } = this.props

    if (hasError) {
      return null // Parent will handle error UI
    }
    return children
  }
}

export default ErrorBoundary
